import React, { useCallback } from "react";
import { useTranslations } from "@jugl-web/utils";
import { useFormContext, useWatch } from "react-hook-form";
import { CRMInfoSection } from "../../../../components/CRMInfoSection";
import { EmailSettingsFormValues } from "../EmailSettingsTab/types";
import { ResponseModeOption } from "./components/ResponseModeOption";
import { ReactComponent as ManualIcon } from "../../assets/manual.svg";
import { ReactComponent as AiAgentIcon } from "../../assets/ai-agent.svg";

type ResponseMode = "manual" | "ai";

export const ResponseModeSection: React.FC = () => {
  const { t } = useTranslations();
  const { control, setValue } = useFormContext<EmailSettingsFormValues>();

  const isAiReplyEnabled = useWatch({ control, name: "ai_reply" });

  const handleSelectMode = useCallback(
    (mode: ResponseMode) => {
      const nextAiReply = mode === "ai";

      if (nextAiReply === isAiReplyEnabled) {
        return;
      }

      setValue("ai_reply", nextAiReply, { shouldDirty: true });
    },
    [isAiReplyEnabled, setValue]
  );

  return (
    <CRMInfoSection
      title={t({
        id: "client-support-page.who-handle-incoming-emails",
        defaultMessage: "Who handle incoming emails?",
      })}
      subtitle={t({
        id: "client-support-page.choose-who-handles-email-responses",
        defaultMessage:
          "Choose who will handle responds to customers via Email",
      })}
    >
      <div className="grid max-w-[900px] grid-cols-2 gap-6 max-lg:grid-cols-1 max-lg:gap-3">
        <ResponseModeOption
          icon={ManualIcon}
          title={t({
            id: "client-support-page.manually-only",
            defaultMessage: "Manually only",
          })}
          description={t({
            id: "cx-ai-agents-page.team-replies-manually",
            defaultMessage: "Team replies manually without AI Assistance",
          })}
          isSelected={!isAiReplyEnabled}
          onSelect={() => handleSelectMode("manual")}
        />
        <ResponseModeOption
          icon={AiAgentIcon}
          title={t({
            id: "client-support-page.ai-agent-responds",
            defaultMessage: "AI Agent responds",
          })}
          description={t(
            {
              id: "cx-ai-agents-page.ai-agent-replies-description",
              defaultMessage:
                "Agent send replies automatically{br}and transfer to human agent if needed",
            },
            { br: " " }
          )}
          isSelected={isAiReplyEnabled}
          onSelect={() => handleSelectMode("ai")}
        />
      </div>
    </CRMInfoSection>
  );
};
